'use client'

import { useCallback, useEffect, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { X, CreditCard } from 'lucide-react'
import { CardNumberElement, CardExpiryElement, CardCvcElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useCurrentPaymentMethodQuery, useUpdatePaymentMethodMutation } from 'graphql/generated/graphql'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { Checkbox } from '@material-tailwind/react'

interface UpdatePaymentModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  userData: {
    firstName: string
    lastName: string
    email: string
    phone: string
    address: string
    city: string
    state: string
    zip: string
  }
}

const cardElementOptions = {
  showIcon: true,
  placeholder: 'Card Number',
  style: {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": { color: "#9CA3AF" },
    },
    invalid: {
      color: "#EF4444",
      iconColor: "#EF4444",
    },
  },
}

export function UpdatePaymentModal({
  open,
  onClose,
  onSuccess,
  userData,
}: UpdatePaymentModalProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [loading, setLoading] = useState(false)
  const [sameAsAccount, setSameAsAccount] = useState(true)
  const [cardComplete, setCardComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  })

  const { data: currentPaymentData, loading: loadingCurrent } = useCurrentPaymentMethodQuery({
    fetchPolicy: 'network-only',
    skip: !open,
  })

  const [updatePaymentMethod] = useUpdatePaymentMethodMutation()

  const currentPayment = currentPaymentData?.currentPaymentMethod

  // Get card brand icon
  const getCardBrandIcon = (brand: string) => {
    const brandLower = brand?.toLowerCase()
    switch (brandLower) {
      case 'visa':
        return '💳'
      case 'mastercard':
        return '💳'
      case 'amex':
        return '💳'
      case 'discover':
        return '💳'
      default:
        return '💳'
    }
  }

  const handleCardChange = (event: any) => {
    setCardComplete((prev) => ({
      ...prev,
      [event.elementType]: event.complete,
    }))
  }

  const resetAddress = useCallback((checked: boolean) => {
    const addressElement = elements?.getElement(AddressElement)
    if (!addressElement) return

    if (checked) {
      addressElement.update({
        defaultValues: {
          name: `${userData.firstName} ${userData.lastName}`,
          phone: userData.phone,
          address: {
            country: 'US',
            city: userData.city,
            line1: userData.address,
            state: userData.state,
            postal_code: userData.zip,
          },
        },
      })
    } else {
      addressElement.clear()
    }
  }, [elements, userData])

  useEffect(() => {
    if (open && sameAsAccount) {
      // Small delay to ensure element is mounted
      setTimeout(() => resetAddress(true), 100)
    }
  }, [open, sameAsAccount, resetAddress])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      toast.error('Stripe has not loaded yet. Please try again.')
      return
    }

    if (!cardComplete.cardNumber || !cardComplete.cardExpiry || !cardComplete.cardCvc) {
      toast.error('Please fill in all card details.')
      return
    }

    setLoading(true)

    try {
      const cardElement = elements.getElement(CardNumberElement)
      const addressElement = elements.getElement(AddressElement)

      // Get address details
      const { complete, value } = await addressElement.getValue()

      if (!complete) {
        toast.error('Please fill in the billing address.')
        setLoading(false)
        return
      }

      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: value.name,
          email: userData.email,
          phone: value.phone,
          address: {
            line1: value.address.line1,
            line2: value.address.line2,
            city: value.address.city,
            state: value.address.state,
            postal_code: value.address.postal_code,
            country: value.address.country,
          },
        },
      })

      if (error) {
        console.error('Stripe error:', error)
        toast.error(error.message || 'Failed to create payment method')
        setLoading(false)
        return
      }

      // Update payment method via GraphQL
      const { data } = await updatePaymentMethod({
        variables: {
          paymentMethodID: paymentMethod.id,
        },
      })

      if (data?.updatePaymentMethod?.success) {
        toast.success('Payment method updated successfully!')
        onSuccess()
        onClose()
      } else {
        toast.error(data?.updatePaymentMethod?.message || 'Failed to update payment method')
      }
    } catch (error) {
      console.error('Update payment error:', error)
      toast.error('An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <style jsx global>{`
        .scrollbar-tiny::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .scrollbar-tiny::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-tiny::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .scrollbar-tiny::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6 scrollbar-tiny">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-gray-100 rounded-full p-2 text-neutral-500 hover:text-neutral-700 transition-colors z-50"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-[#1a1a1a]">Update Payment Method</h2>
          <p className="text-sm text-[#2d3748] mt-2">
            Update your payment information for your Professional subscription
          </p>
        </div>

        {/* Current Payment Method */}
        <div className="mb-6">
          <label className="text-sm font-medium text-[#2d3748] mb-2 block">
            Current Payment Method
          </label>
          {loadingCurrent ? (
            <div className="border border-[#CBD7DD] rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            </div>
          ) : currentPayment ? (
            <div className="border border-[#CBD7DD] rounded-lg p-4 bg-white">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="font-medium">
                    {currentPayment.brand} •••• •••• •••• {currentPayment.last4}
                  </div>
                  <div className="text-sm text-gray-500">
                    Expires {currentPayment.expMonth}/{currentPayment.expYear}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-[#CBD7DD] rounded-lg p-4 bg-gray-50">
              <span className="text-sm text-gray-500">No payment method on file</span>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* New Payment Method */}
          <div className="mb-4">
            <label className="text-sm font-medium text-[#2d3748] mb-2 block">
              New Payment Method
            </label>

            {/* Card Number */}
            <div className="mb-3">
              <label className="text-sm text-gray-600 mb-1 block">Card Number</label>
              <div className="border border-[#CBD7DD]/40 rounded-lg p-3 focus-within:border-2 focus-within:border-[#6EAC36]">
                <CardNumberElement
                  className="py-0.5 w-full"
                  options={cardElementOptions}
                  onChange={handleCardChange}
                />
              </div>
            </div>

            {/* Expiry and CVC */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-sm text-gray-600 mb-1 block">Expiry Date</label>
                <div className="border border-[#CBD7DD]/40 rounded-lg p-3 focus-within:border-2 focus-within:border-[#6EAC36]">
                  <CardExpiryElement
                    className="py-0.5 w-full"
                    onChange={handleCardChange}
                  />
                </div>
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-600 mb-1 block">CVC</label>
                <div className="border border-[#CBD7DD]/40 rounded-lg p-3 focus-within:border-2 focus-within:border-[#6EAC36]">
                  <CardCvcElement
                    className="py-0.5 w-full"
                    onChange={handleCardChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[#2d3748]">
                Billing Address
              </label>
              <div className="flex items-center">
                <Checkbox
                  checked={sameAsAccount}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setSameAsAccount(checked)
                    resetAddress(checked)
                  }}
                  className="w-5 h-5 checked:outline-none checked:ring-2 checked:bg-[#6EAC36] checked:ring-[#6EAC36]"
                />
                <label className="ml-2 text-sm">Same as account address</label>
              </div>
            </div>
            <AddressElement
              options={{
                mode: 'billing',
                allowedCountries: ['US'],
                fields: {
                  phone: 'always',
                },
                validation: {
                  phone: {
                    required: 'always',
                  },
                },
                defaultValues: {
                  name: `${userData.firstName} ${userData.lastName}` || '',
                  phone: userData.phone || '',
                  address: {
                    country: 'US',
                    city: userData.city || '',
                    line1: userData.address || '',
                    state: userData.state || '',
                    postal_code: userData.zip || '',
                  },
                },
                autocomplete: {
                  mode: 'google_maps_api',
                  apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY!,
                },
              }}
              className="py-1 w-full"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={loading || !cardComplete.cardNumber || !cardComplete.cardExpiry || !cardComplete.cardCvc}
              className="w-full px-5 py-3 bg-[#6EAC36] text-white font-medium rounded-[999px] hover:bg-[#5a8f2d] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="mr-2 h-5 w-5 animate-spin"
                  />
                  Updating...
                </>
              ) : (
                'Update Payment Method'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full px-5 py-3 bg-gray-200 text-gray-700 font-medium rounded-[999px] hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
